import { type Ref, useImperativeHandle } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridRow, DataGridTable } from "./columns/types";
import { runGridAction } from "./focus/gridActions";
import type {
    DataGridScrollAlign,
    GridController,
} from "./focus/gridController";
import type { GridKeyAction } from "./focus/keyMap";
import {
    findFirstFocusableRowIndex,
    selectFocusedRowId,
} from "./focus/navigation";
import { focusTableRow } from "./focus/useAutoFocus";
import type { DataGridInstance } from "./types";

/** Imperative handle passed through `ref`. */
export interface DataGridHandle {
    /**
     * Expands the row's collapsed parents, then scrolls it into view.
     * `align` defaults to "auto": the least scroll that shows the row.
     */
    scrollToRow: (
        rowId: string,
        options?: { align?: DataGridScrollAlign },
    ) => void;
    /** Like `scrollToRow`, and scrolls the cell into view horizontally. Doesn't move focus. */
    scrollToCell: (rowId: string, columnId: string) => void;
    /** Focuses a cell, or the row when `columnId` is omitted. */
    focusCell: (rowId: string, columnId?: string) => void;
    expandAll: () => void;
    collapseAll: () => void;
    /** Moves row focus as ArrowDown/ArrowUp/Mod+Home/Mod+End would, but "previous" stays on the first row; from no focus, every target lands on the first row. */
    focusRow: (target: FocusRowTarget) => void;
    /** The focused (or last focused) row's id, or null. */
    getFocusedRowId: () => string | null;
}

type FocusRowTarget = "next" | "previous" | "first" | "last";

const FOCUS_ROW_ACTIONS: Record<FocusRowTarget, GridKeyAction> = {
    next: { type: "move", direction: "down" },
    previous: { type: "move", direction: "up" },
    first: { type: "gridStart" },
    last: { type: "gridEnd" },
};

/** Expands every collapsed parent of the row in one update (controlled state sees one change). */
const expandAncestors = <TData extends RowData>(
    table: DataGridTable<TData>,
    rowId: string,
) => {
    const row: DataGridRow<TData> | undefined =
        table.getCoreRowModel().rowsById[rowId];
    const collapsedIds = (row?.getParentRows() ?? [])
        .filter((parent) => !parent.getIsExpanded())
        .map((parent) => parent.id);
    if (collapsedIds.length === 0) {
        return;
    }
    table.setExpanded((expanded) =>
        expanded === true
            ? expanded
            : {
                  ...expanded,
                  ...Object.fromEntries(collapsedIds.map((id) => [id, true])),
              },
    );
};

export const useTableHandle = <TData extends RowData>({
    ref,
    instance,
    controller,
}: {
    ref: Ref<DataGridHandle> | undefined;
    instance: DataGridInstance<TData>;
    controller: GridController;
}) => {
    const { table } = instance;
    useImperativeHandle(ref, (): DataGridHandle => {
        const getFocusedRowId = () =>
            selectFocusedRowId(table.atoms.cellSelection.get());
        return {
            scrollToRow: (rowId, options) => {
                expandAncestors(table, rowId);
                controller.scrollTo({
                    rowId,
                    columnId: null,
                    align: options?.align ?? "auto",
                });
            },
            scrollToCell: (rowId, columnId) => {
                expandAncestors(table, rowId);
                controller.scrollTo({ rowId, columnId, align: "auto" });
            },
            focusCell: (rowId, columnId) =>
                focusTableRow({ instance, controller, rowId, columnId }),
            expandAll: () => table.toggleAllRowsExpanded(true),
            collapseAll: () => table.toggleAllRowsExpanded(false),
            focusRow: (target) => {
                const rowId = getFocusedRowId();
                const isLeavingFirstRowUp =
                    target === "previous" &&
                    rowId !== null &&
                    controller.rowIndexById.get(rowId) ===
                        findFirstFocusableRowIndex(
                            table.getRowModel().rows,
                            instance.rowKinds.isSeparator,
                        );
                if (controller.root && !isLeavingFirstRowUp) {
                    runGridAction({
                        action: FOCUS_ROW_ACTIONS[target],
                        instance,
                        controller,
                        target: controller.root,
                    });
                }
            },
            getFocusedRowId,
        };
    }, [controller, instance, table]);
};
